import { RNG } from "./rng.js";
import { activationFunctions } from "./activation.js"
import { NetworkOutput } from "./networkOutput.js"
import { Layer } from "./layer.js"

class Network {
  // private fields
  #zeroNabla

  constructor(sizes, params) {
    var defaults = {
      seed: 42,
      activation: activationFunctions.sigmoid,
      layers: null
    }
    params = {...defaults, ...params}

    this.layers = []

    // number of perceptrons in each layer
    this.sizes = sizes

    // inputs to first layer
    this.nInputs = this.sizes[0]

    // outputs from last layer
    this.nOutputs = this.sizes[this.sizes.length - 1]

    if(params.layers) {
      this.layers = params.layers
    }
    else {
      // add layers to network
      for(let i = 1; i < sizes.length; i++) {
        this.layers.push(new Layer(sizes[i], sizes[i - 1], {seed: params.seed+i, activation: params.activation}))
      }
    }

    // starting nablas (used for training)
    this.#zeroNabla = {
      w: this.layers.map(l => l.perceptrons.map(p => Array(p.weights.length).fill(0))),
      b: this.layers.map(l => l.perceptrons.map(p => 0)), 
    }
  }
  
  // load network from object
  static load(data) {
    return new Network(data.sizes, {
      layers: data.layers.map(l => Layer.load(l))
    })
  }

  // export network to serializable format
  dump() {
    return {
      sizes: this.sizes,
      layers: this.layers.map(l => l.dump())
    }
  }

  getZeroNabla() {
    return {
      w: this.layers.map(l => l.perceptrons.map(p => Array(p.weights.length).fill(0))),
      b: this.layers.map(l => l.perceptrons.map(p => 0)), 
    } 
  }

  // get output of neural network
  evaluate(inputs, params) {
    const defaults = {
      verbose: true
    }
    params = {...defaults, ...params}

    // map of outputs (activations) 
    var outputMap = [inputs]

    // map of z (before activation function applied)
    var zMap = []

    for(let i = 0; i < this.layers.length; i++) {
      let l = this.layers[i]

      // verbose outputs
      var vOutput = l.evaluate(inputs, {verbose: true})

      // set outputs (inputs for next iteration)
      inputs = vOutput.map(o => o.activation)

      // set zs
      var zs = vOutput.map(o => o.z)

      // update maps (2D)
      outputMap.push(inputs)
      zMap.push(zs)
    }
    

    return new NetworkOutput({
      outputs: inputs,
      outputMap: outputMap,
      zMap: zMap
    })
  }

  // get map of weights
  getWeights() {
    var weights = []
    for(let l=0; l<this.layers.length; l++) {
      var layerWeights = []
      var layer = this.layers[l]
      for(let j=0; j<layer.perceptrons.length; j++) {
        layerWeights.push(layer.perceptrons[j].weights)
      }
      weights.push(layerWeights)
    }
    return weights
  }

  // get map of biases
  getBiases() {
    var biases = []
    for(let l=0; l<this.layers.length; l++) {
      var layerBiases = []
      var layer = this.layers[l]
      for(let j=0; j<layer.perceptrons.length; j++) {
        layerBiases.push(layer.perceptrons[j].bias)
      }
      biases.push(layerBiases)
    }
    return biases
  }

  // iterate over each layer
  iterate(f) {
    for(layer in this.layers) {
      f(layer)
    }
  }

  // use stochaistic gradient descent to train network
  train(training_data, params) {
    var defaults = {
      epochs: 10,
      batch_size: 5,
      learning_rate: 3,
      seed: 42,
      callback: null
    }
    var params = {...defaults, ...params}


    // shuffle training data
    var rng = new RNG(params.seed)
    training_data = rng.shuffle(training_data)


    for(let i=0; i<params.epochs; i++) {
      var batch = 1 // for logging only
      for(let j=0; j<Math.floor(training_data.length/params.batch_size); j+=params.batch_size) {
        this.train_batch(training_data.slice(j, j+params.batch_size), params.learning_rate)
        if(params.callback) {
          params.callback({
            batch: batch++,
            epoch: i,
            nEpochs: params.epochs
          })
        }
      }
    }
    console.log('training complete')
  }

  // train on one mini-batch of examples
  train_batch(batch, learning_rate) {
    var nabla = this.getZeroNabla()

    for(var i=0; i<batch.length; i++) {
      var b = batch[i]

      var delta_nablas = this.backprop(b.X, b.y)

      // change nablas for batch using backprop
      for(let l=0; l<nabla.w.length; l++) {

        for(let j=0; j<nabla.w[l].length; j++) {
          nabla.b[l][j] += delta_nablas.b[l][j]
          for(let k=0; k<nabla.w[l][j].length; k++) {
            nabla.w[l][j][k] += delta_nablas.w[l][j][k]
          }
        }
      }

    }

    // update network
    for(let l=0; l<nabla.w.length; l++) {
      for(let j=0; j<nabla.w[l].length; j++) {
        this.layers[l].perceptrons[j].bias -= nabla.b[l][j] * (learning_rate / batch.length)
        for(let k=0; k<nabla.w[l][j].length; k++) {
          this.layers[l].perceptrons[j].weights[k] -= nabla.w[l][j][k] * (learning_rate / batch.length)
        }
      }
    }
  }

  // run backpropagation
  backprop(x, y) {
    // get nablas initliazed to zero
    var zeroNabla = this.getZeroNabla() 
    var nablaB = zeroNabla.b
    var nablaW = zeroNabla.w

    // feed-forward
    const output = this.evaluate(x)


    // output map
    const outputMap = output.outputMap


    // index of last layer
    const ll = outputMap.length - 2

    // derivative of loss wrt each activation
    var d_cost = output.loss_derivative(y)

    // calculate output layer error
    var deltas = []
    for(let i = 0; i < y.length; i++) {
      // d_cost/d_z = d_cost/d_activation * d_activation/d_z
      deltas.push(d_cost[i] * this.layers[ll].perceptrons[i].activation.d(output.zMap[ll][i]))
    }

    for(let l=ll; l >= 0; l--) {
      // update bias nablas
      nablaB[l] = deltas

      if(l == 0) {
        var nInputs = this.nInputs
      }
      else {
        var nInputs = outputMap[l-1].length 
      }

      // update weight nablas
      for(let j=0; j < this.layers[l].size; j++) {
        // error at current neuron
        var current_error = deltas[j]
        for(let k=0; k < nInputs; k++) {
          nablaW[l][j][k] = outputMap[l][k] * current_error
        }
      }
      // update deltas
      if(l > 0) {
        var new_deltas = []
        for(let k=0; k < this.layers[l-1].size; k++) {
          // calculate delta for j-th neuron in next layer
          var new_delta = 0
          for(let j=0; j < this.layers[l].size; j++) {
            var perceptron = this.layers[l].perceptrons[j]
            new_delta += perceptron.weights[k] * deltas[j] * perceptron.activation.d(output.zMap[l-1][k])
          }
          new_deltas.push(new_delta)
        }
        deltas = new_deltas
      }
    }
    return {
      b: nablaB,
      w: nablaW
    }
  }
}

export {
  Network
}